import React, {
  useContext,
  useEffect,
} from 'react';
import { flowRight } from 'lodash';
import { ELSURLHelper } from '@els/els-ui-common-react';
import {
  ELSPropsFromModalService,
  ELSWithModalService
} from '@els/els-component-modal-react';
import {
  ELSPropsFromToastService,
  ELSWithToastService
} from '@els/els-component-toast-react';
import FocusTrap from 'focus-trap-react';
import {
  useHistory,
  useLocation
} from 'react-router';
import { FlexLayout } from '../../components/flex/FlexLayout.component';
import { FlexItem } from '../../components/flex/FlexItem.component';
import { FlexLayoutModifier } from '../../components/flex/flex.constants';
import RemediationHeader from '../../components/remediation-header/RemediationHeader.component';
import {
  RecommendationDto,
  RemediationActivityTypeDto,
  RemediationActivityTypeSettings,
} from '../../apis/nhe-remediation-service/nhe-remediation-service.dtos';
import RemediationQuizModal, { RemediationQuizModalId } from './RemediationQuizModal.component';
import RemediationActivityProgress from '../../components/remediation-activity-progress/RemediationActivityProgress.component';
import RemediationActivitySection from './RemediationActivitySection.component';
import { AssessmentGoalDto } from '../../apis/eols-assessment-service/eols-assessment-service.dtos';
import RemediationActivityModal, { RemediationActivityModalId } from './RemediationActivityModal.component';
import { getReturnLinkId } from '../../utilities/app.utilities';
import { AppContext } from '../../components/app/AppProvider.component';
import {
  appLinkReturnAction,
  patchRaasAssessmentLastAccessedAction,
  trackAdobeAction,
  trackNewRelicAction
} from '../../components/app/app-state-actions.utilities';
import {
  AdobeAnalyticsAction,
  AdobeAnalyticsButtonType
} from '../../models/adobe-analytics.models';
import { NewRelicAnalyticsAction } from '../../models/new-relic-analytics.models';
import {
  getActiveAssignmentActivityTypeSettings,
  getAssessmentGoals,
  getCompletedDate,
  getDueDate,
  getHourAndMinuteFromSecond,
  getNextRecommendation,
  getRecommendationTitle,
  getTotalProgress,
  getTotalTimeSpentFromAssessmentMetrics,
  isRecommendationPartOfGoal,
  isTotalPLPComplete
} from '../../utilities/remediation.utilities';
import { StatusMessage } from '../../components/status-message/StatusMessage.component';
import DueDateProgress from '../../components/due-date-progress/DueDateProgress.component';
import {
  getProgressDisplay,
  getStatus
} from '../../components/due-date-progress/due-date-progress.utilities';
import { getStudentHomePageTitle } from './remediation-home.utilities';
import cx from 'classnames';
import { SherpathAIChatSidebar } from '../../components/sherpath-ai-chat-sidebar/SherpathAIChatSidebar.component';
import {
  buildSherpathAIPageContext,
  PageContextType
} from '../../utilities/sherpath-ai-context.utilities';
import { getIsAIChatPageContextEnabled } from '../../components/app/app-state-selectors.utilities';
import { CompleteBannerHomePage } from '../../components/complete-banner/CompleteBannerHomePage.component';
import { ELSButton } from '@els/els-component-button-react';
import {
  ELSButtonSize,
  ELSButtonType
} from '../../models/button.models';
import { RoutePath } from '../../components/app/app.constants';

type RemediationAssignmentPropsOnly = {
};

export type RemediationAssignmentProps = RemediationAssignmentPropsOnly
  & ELSPropsFromModalService
  & ELSPropsFromToastService;

// eslint-disable-next-line sonarjs/cognitive-complexity
export const RemediationAssignmentComponent = (props: RemediationAssignmentProps): JSX.Element => {

  const {
    appState,
    setAppState,
    appStateRef
  } = useContext(AppContext);
  const location = useLocation();
  const history = useHistory();
  const baseState = appState.remediationBaseState;

  const handleAssessLaunch = (fnProps: {
    recommendation: RecommendationDto;
    assessmentGoal: AssessmentGoalDto;
  }) => {

    const {
      recommendation,
    } = fnProps;

    const {
      modalService,
    } = props;

    if (!recommendation) {
      return;
    }

    modalService.openCustomModal({
      modalId: RemediationQuizModalId,
      modal: (
        <FocusTrap>
          <RemediationQuizModal
            assessmentGoal={fnProps.assessmentGoal}
            recommendation={recommendation}
          />
        </FocusTrap>
      )
    });
  };

  const goToAssessmentPostScreen = () => {

    const isOpenAssessModal = ELSURLHelper.getParameterByName('isOpenAssessModal', location.search);

    // Need all this before opening the quiz modal
    if (
      !isOpenAssessModal
      || !appState.appLinkData
      || !appState.appLinkData.outPostBody
      || !baseState
      || !baseState.recommendations
      || !baseState.eolsAssessment
      || !baseState.recommendationAttempts
    ) {
      return;
    }

    const recommendation = baseState.recommendations.find((_recommendation: RecommendationDto) => {
      return _recommendation.id === appState.appLinkData.outPostBody.recommendationId;
    });

    if (!recommendation) {
      return;
    }
    const assessmentGoal = baseState.eolsAssessment?.assessmentGoals?.find((goal) => {
      return isRecommendationPartOfGoal(recommendation, goal);
    });

    if (!assessmentGoal) {
      return;
    }

    handleAssessLaunch({
      recommendation,
      assessmentGoal
    });
  };

  useEffect(() => {
    goToAssessmentPostScreen();
  }, [location.search, baseState]);

  useEffect(() => {
    if (!baseState?.raasAssessment) {
      return;
    }

    patchRaasAssessmentLastAccessedAction({ appState, appStateRef, setAppState, raasAssessmentId: baseState.raasAssessment.id });
  }, [baseState?.raasAssessment?.id]);

  const getPageContext = (): string => {
    if (!baseState?.remediationAssignmentWithSources) {
      return null;
    }

    if (!getIsAIChatPageContextEnabled(appState)) {
      return null;
    }

    // Mirror the live home-page UI so the chat can describe what the student is
    // actually seeing (status header, recommendation card) rather than guessing.
    const homeTotalProgress = getTotalProgress(baseState);
    const homeIsComplete = isTotalPLPComplete(baseState);
    const homeDueDate = getDueDate(baseState);
    const homeDateCompleted = homeIsComplete ? getCompletedDate(baseState) : null;
    const homeStatusDisplay = getProgressDisplay({
      status: getStatus({ dueDate: homeDueDate, isComplete: homeIsComplete, dateCompleted: homeDateCompleted }),
      dateCompleted: homeDateCompleted,
      dueDate: homeDueDate
    });
    // The recommendation card is only shown while the PLP is incomplete and a
    // next recommendation exists (see the card render guard below).
    const homeNextRecommendation = !homeIsComplete ? getNextRecommendation(baseState, null) : null;
    const homeRecommendationLabel = homeNextRecommendation
      ? (homeTotalProgress === 0 ? 'Not sure where to start?' : 'Not sure what to do next?')
      : null;
    const homeTopRecommendationTitle = homeNextRecommendation
      ? getRecommendationTitle(homeNextRecommendation)
      : null;
    // Mirror the exact time-spent value the top-right header renders (see
    // DueDateProgress.component.tsx), including the "0 min" case, so the chat
    // reports the real value instead of disclaiming access.
    const homeTimeSpent = getHourAndMinuteFromSecond(getTotalTimeSpentFromAssessmentMetrics(baseState?.assessmentMetrics));

    return buildSherpathAIPageContext({
      remediationAssignmentWithSources: baseState.remediationAssignmentWithSources,
      assessmentMetrics: baseState?.assessmentMetrics,
      assessmentSources: baseState?.raasAssessmentSourceList,
      hesiExamInfo: baseState?.hesiExamInfo,
      totalProgressPercent: homeTotalProgress,
      pageType: PageContextType.HOME,
      pageSpecificData: null,
      homePageData: {
        statusDisplay: homeStatusDisplay,
        recommendationLabel: homeRecommendationLabel,
        topRecommendationTitle: homeTopRecommendationTitle,
        timeSpent: homeTimeSpent
      }
    });
  };

  const handleReturnNavigation = () => {
    appLinkReturnAction({
      appState,
      appStateRef,
      setAppState,
      linkId: getReturnLinkId(appState.appLinkData, appState.appLinkCookies),
      returnPostBody: null
    });
  };

  const handleTaxonClick = (assessmentGoal: AssessmentGoalDto) => {

    if (assessmentGoal.type === RemediationActivityTypeDto.ASSESS_BY_CONTENT_TYPE) {

      const goalRecommendation = baseState.recommendations?.find((recommendation) => {
        return isRecommendationPartOfGoal(recommendation, assessmentGoal);
      });

      if (!goalRecommendation) {
        // eslint-disable-next-line no-console
        console.log('No recommendation found for goal');
        return;
      }

      handleAssessLaunch({
        recommendation: goalRecommendation,
        assessmentGoal
      });
      return;
    }

    const {
      modalService,
    } = props;

    modalService.openCustomModal({
      modalId: RemediationActivityModalId,
      modal: (
        <FocusTrap>
          <RemediationActivityModal
            assessmentGoal={assessmentGoal}
          />
        </FocusTrap>
      )
    });

  };

  const handleViewNextRecClick = (nextRec: RecommendationDto) => {
    trackAdobeAction({
      appStateRef,
      actionProps: {
        action: AdobeAnalyticsAction.BUTTON_CLICK,
        pageData: {
          buttonType: `plp:${AdobeAnalyticsButtonType.BUTTON}:viewNextRecommendation`,
        }
      }
    });
    trackNewRelicAction({
      appStateRef,
      actionProps: {
        action: NewRelicAnalyticsAction.VIEW_NEXT_RECOMMENDATION_CLICKED,
        props: {
          recommendationId: nextRec?.id,
          contentType: nextRec?.contentType,
          remediationAssignmentId: baseState.remediationAssignmentWithSources?.remediationAssignment?.id,
          assessmentId: baseState.eolsAssessment?.id
        }
      }
    });
    history.push(RoutePath.REMEDIATION_NEXT_RECOMMENDATION);
  }

  if (!baseState) {
    return null;
  }

  const nextRecommendation = getNextRecommendation(baseState, null)
  const totalProgress = getTotalProgress(baseState);
  const isComplete = isTotalPLPComplete(baseState);
  const activeActivityTypes = getActiveAssignmentActivityTypeSettings(baseState);

  if (baseState.showPendingAssessmentMessage) {
    return (
      <div className="c-nra-remediation-home">
        <RemediationHeader
          handleReturnNavigation={handleReturnNavigation}
          showSherpathAIChatToggle={true}
        />

        <FlexLayout modifiers={[FlexLayoutModifier.CENTER]}>
          <FlexItem>
            <div className="c-nra-remediation-home__main u-els-margin-top-3x">
              <div className="c-nra-remediation-home__center">
                <StatusMessage raasAssessment={baseState.raasAssessment} />
              </div>
            </div>
          </FlexItem>
        </FlexLayout>
      </div>
    );
  }

  return (
    <>
      <div className="c-nra-remediation-home c-nra-remediation-home--background-student">
        <RemediationHeader
          handleReturnNavigation={handleReturnNavigation}
          showSherpathAIChatToggle={true}
        />

        <div className="c-nra-remediation-home__main">

          <div className="c-nra-remediation-home__body">

            <div className={
              cx('c-nra-remediation-home__page', {
                'c-nra-remediation-home__page--chat-active': appState.isChatActive,
              })
            }
            >

              <div className="c-nra-remediation-home__center">
                {
                  baseState.recommendations
                  && baseState.recommendations.length > 0
                  && baseState.remediationAssignmentWithSources
                  && (
                    <>
                      <div className="c-nra-remediation-home__main-header">
                        <div className="u-els-margin-bottom">

                          <FlexLayout modifiers={[
                            FlexLayoutModifier.GUTTERS_2X,
                            FlexLayoutModifier.MIDDLE,
                          ]}>
                            <FlexItem modifiers={[FlexLayoutModifier.GROW, FlexLayoutModifier.TOP, FlexLayoutModifier.MIDDLE]}>
                              <div>
                                {appState.eolsUser.firstName} {appState.eolsUser.lastName}
                              </div>
                              <h3 className="u-els-font-family-bold">
                                {getStudentHomePageTitle(baseState)}
                              </h3>
                            </FlexItem>

                            <FlexItem>
                              <DueDateProgress />
                            </FlexItem>
                          </FlexLayout>
                        </div>

                        {isComplete && (
                          <div className="u-els-margin-bottom">
                            <CompleteBannerHomePage />
                          </div>
                        )}

                        {!isComplete && (
                          <div>
                            You took the exam, and now its time to make sure you’re on top of your game for next time.
                            We&apos;ve analyzed your results and have put together a learning plan personalized to your specific
                            needs.
                          </div>
                        )}

                      </div>

                      {activeActivityTypes.length > 1 && (
                        <div className="o-els-container o-els-container--2x">
                          <FlexLayout
                            modifiers={[
                              FlexLayoutModifier.GUTTERS_2X,
                              FlexLayoutModifier.WRAP_AT_MOBILE
                            ]}
                          >
                            {
                              activeActivityTypes
                                .map((activityType: RemediationActivityTypeSettings) => {
                                  return (
                                    <FlexItem
                                      modifiers={[FlexLayoutModifier.GROW]}
                                      key={activityType.activityType}
                                      classes={['u-els-width-1o1@mobile']}
                                    >
                                      <RemediationActivityProgress
                                        activityType={activityType.activityType}
                                        assessmentGoals={getAssessmentGoals(baseState?.eolsAssessment)}
                                      />
                                    </FlexItem>
                                  );
                                })
                            }
                          </FlexLayout>
                        </div>
                      )}

                      {!isComplete && nextRecommendation && (
                        <div className="c-nra-remediation-activity-section u-els-margin-bottom-2x">
                          <FlexLayout modifiers={[FlexLayoutModifier.GUTTERS, FlexLayoutModifier.MIDDLE]}>
                            <FlexItem modifiers={[FlexLayoutModifier.GROW]}>
                              <h3>
                                {
                                  totalProgress === 0
                                    ? 'Not sure where to start?'
                                    : 'Not sure what to do next?'
                                }
                              </h3>
                              <p>See your top recommended activity.</p>
                            </FlexItem>
                            <FlexItem>
                              <ELSButton
                                type={ELSButtonType.SECONDARY}
                                size={ELSButtonSize.SMALL}
                                onClick={() => handleViewNextRecClick(nextRecommendation)}
                              >
                                View
                              </ELSButton>
                            </FlexItem>
                          </FlexLayout>
                        </div>
                      )}

                      <div>
                        {
                          getActiveAssignmentActivityTypeSettings(baseState)
                            .map((activityType: RemediationActivityTypeSettings) => {
                              return (
                                <div
                                  className="c-nra-remediation-home__tab"
                                  key={activityType.activityType}
                                >
                                  <div className="c-nra-remediation-home__tab-content">
                                    <RemediationActivitySection
                                      activityType={activityType.activityType}
                                      onTaxonClick={handleTaxonClick}
                                    />
                                  </div>
                                </div>
                              );
                            })
                        }
                      </div>
                    </>
                  )
                }
              </div>
            </div>

            <SherpathAIChatSidebar
              remediationAssignmentWithSources={baseState?.remediationAssignmentWithSources}
              pageContext={getPageContext()}
              pageContextType={PageContextType.HOME}
            />
          </div>
        </div>
      </div>
    </>
  );
};

const enhance = flowRight(
  ELSWithModalService,
  ELSWithToastService,
);

const RemediationAssignment = enhance(RemediationAssignmentComponent as unknown as React.FunctionComponent<unknown>) as unknown as React.FC<RemediationAssignmentPropsOnly>;

export default RemediationAssignment;
